import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe,
  Send,
  Phone,
  MessageSquare,
  HelpCircle,
  Users,
  Calendar,
  ShoppingCart,
  Mail,
  Shield,
  Upload,
  X,
  FileText,
  ArrowRight,
  Check,
  Copy,
  ExternalLink,
  Sparkles,
  Plus,
  Type,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';
import apiClient from '@/services/api';
import { agentsService } from '@/services/agents.service';
import { channelsService } from '@/services/channels.service';
import { knowledgeService } from '@/services/knowledge.service';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type MessageSender = 'bot' | 'user';
type MessageType =
  | 'text'
  | 'options'
  | 'channels'
  | 'channel-setup-telegram'
  | 'channel-setup-messenger'
  | 'channel-setup-whatsapp'
  | 'channel-setup-web'
  | 'knowledge'
  | 'skills'
  | 'done';

interface ChatMessage {
  id: string;
  sender: MessageSender;
  content: string;
  type: MessageType;
  meta?: Record<string, unknown>;
}

type ConversationPhase =
  | 'org-name'
  | 'channels'
  | 'channel-setup'
  | 'channel-token-input'
  | 'knowledge'
  | 'agent-name'
  | 'agent-tone'
  | 'skills'
  | 'complete';

interface OnboardingData {
  orgName: string;
  channels: string[];
  channelTokens: Record<string, string>;
  knowledgeFiles: { name: string; content: string }[];
  pastedText: string;
  agentName: string;
  agentTone: 'professional' | 'friendly' | 'casual' | '';
  selectedSkills: string[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CHANNEL_OPTIONS = [
  { id: 'telegram', name: 'Telegram', icon: Send, color: 'from-sky-400 to-sky-500', enabled: true },
  { id: 'messenger', name: 'Messenger', icon: MessageSquare, color: 'from-violet-500 to-violet-600', enabled: false },
  { id: 'whatsapp', name: 'WhatsApp', icon: Phone, color: 'from-green-500 to-green-600', enabled: false },
  { id: 'web', name: 'Web Widget', icon: Globe, color: 'from-blue-500 to-blue-600', enabled: true },
];

const SKILL_OPTIONS = [
  { id: 'faq', name: 'FAQ', icon: HelpCircle },
  { id: 'lead', name: 'Lead Qual', icon: Users },
  { id: 'appointment', name: 'Appointments', icon: Calendar },
  { id: 'order', name: 'Orders', icon: ShoppingCart },
  { id: 'support', name: 'Support', icon: Mail },
  { id: 'guard', name: 'Content Guard', icon: Shield },
];

const BOT_TYPING_DELAY = 800;

const PHASE_PROGRESS: Record<string, number> = {
  'org-name': 10,
  'channels': 30,
  'channel-setup': 40,
  'channel-token-input': 45,
  'knowledge': 55,
  'agent-name': 70,
  'agent-tone': 80,
  'skills': 90,
  'complete': 100,
};

const defaultData: OnboardingData = {
  orgName: '',
  channels: [],
  channelTokens: {},
  knowledgeFiles: [],
  pastedText: '',
  agentName: '',
  agentTone: '',
  selectedSkills: [],
};

let _msgId = 0;
function nextId(): string {
  _msgId += 1;
  return `msg-${_msgId}`;
}

// ---------------------------------------------------------------------------
// Typing indicator
// ---------------------------------------------------------------------------

const TypingIndicator: React.FC = () => (
  <div className="flex items-end gap-2.5" style={{ animation: 'fadeSlideUp 0.3s ease-out both' }}>
    <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center shrink-0 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]">
      <Sparkles className="w-4 h-4 text-white" />
    </div>
    <div className="px-4 py-3 rounded-xl rounded-bl-md bg-white border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]">
      <div className="flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-indigo-400" style={{ animation: 'typingBounce 1.2s infinite ease-in-out' }} />
        <span className="w-2 h-2 rounded-full bg-purple-400" style={{ animation: 'typingBounce 1.2s infinite ease-in-out 0.2s' }} />
        <span className="w-2 h-2 rounded-full bg-pink-400" style={{ animation: 'typingBounce 1.2s infinite ease-in-out 0.4s' }} />
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Bubbles
// ---------------------------------------------------------------------------

const BotBubble: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex items-end gap-2.5" style={{ animation: 'fadeSlideUp 0.3s ease-out both' }}>
    <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center shrink-0 border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]">
      <Sparkles className="w-4 h-4 text-white" />
    </div>
    <div className="max-w-[80%] px-4 py-3 rounded-xl rounded-bl-md bg-white border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] text-[15px] leading-relaxed text-gray-800 relative overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-indigo-500 rounded-l-xl" />
      <div className="pl-1.5">{children}</div>
    </div>
  </div>
);

const UserBubble: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex justify-end" style={{ animation: 'fadeSlideUp 0.3s ease-out both' }}>
    <div className="max-w-[80%] px-4 py-3 rounded-xl rounded-br-md bg-blue-500 text-white text-[15px] leading-relaxed border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]">
      {children}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Instruction step component (used inside channel setup cards)
// ---------------------------------------------------------------------------

const InstructionStep: React.FC<{ step: number; text: string; highlight?: string }> = ({ step, text, highlight }) => (
  <div className="flex gap-3 items-start">
    <div className="w-6 h-6 rounded-full bg-blue-100 border-2 border-[#1a1a2e] text-indigo-600 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
      {step}
    </div>
    <p className="text-sm text-gray-700 leading-relaxed">
      {text}
      {highlight && (
        <code className="ml-1 px-1.5 py-0.5 bg-indigo-50 border border-indigo-100 rounded text-xs font-mono text-indigo-700">{highlight}</code>
      )}
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [phase, setPhase] = useState<ConversationPhase>('org-name');
  const [data, setData] = useState<OnboardingData>(defaultData);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [inputMode, setInputMode] = useState<'text' | 'password' | 'none'>('none');
  const [inputPlaceholder, setInputPlaceholder] = useState('');
  const [showInput, setShowInput] = useState(false);

  // For channels & skills interactive cards
  const [pendingChannels, setPendingChannels] = useState<string[]>([]);
  const [pendingSkills, setPendingSkills] = useState<string[]>([]);
  // Channel setup queue
  const [channelSetupQueue, setChannelSetupQueue] = useState<string[]>([]);
  const [currentSetupChannel, setCurrentSetupChannel] = useState('');
  // For knowledge
  const [pendingFiles, setPendingFiles] = useState<{ name: string; content: string }[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [pasteTextInput, setPasteTextInput] = useState('');
  const [pasteTextCount, setPasteTextCount] = useState(0);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasStarted = useRef(false);

  // ---- helpers ----

  const update = useCallback(
    (partial: Partial<OnboardingData>) => setData((prev) => ({ ...prev, ...partial })),
    [],
  );

  const addMessage = useCallback((msg: Omit<ChatMessage, 'id'>) => {
    const newMsg: ChatMessage = { ...msg, id: nextId() };
    setMessages((prev) => [...prev, newMsg]);
    return newMsg;
  }, []);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  useEffect(() => {
    if (showInput) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [showInput, phase]);

  /** Show typing indicator, then deliver bot messages in sequence, then run callback */
  const botSays = useCallback(
    (texts: string[], then?: () => void) => {
      let cumulativeDelay = 0;

      for (let i = 0; i < texts.length; i++) {
        const text = texts[i];
        const typingDelay = cumulativeDelay;
        setTimeout(() => setIsTyping(true), typingDelay);
        cumulativeDelay += BOT_TYPING_DELAY;

        const msgDelay = cumulativeDelay;
        setTimeout(() => {
          setIsTyping(false);
          addMessage({ sender: 'bot', content: text, type: 'text' });
        }, msgDelay);

        if (i < texts.length - 1) {
          cumulativeDelay += 400;
        }
      }

      if (then) {
        setTimeout(then, cumulativeDelay + 100);
      }
    },
    [addMessage],
  );

  // ---- Conversation script - kick off ----

  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    botSays(
      [
        "Hey there! I'm Ori, your setup assistant. Let's get you up and running -- it'll only take a minute.",
        "First off, what's your company or team called?",
      ],
      () => {
        setInputMode('text');
        setInputPlaceholder('Type your company name...');
        setShowInput(true);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Handle user submit from text input ----

  const handleInputSubmit = () => {
    const value = inputValue.trim();
    if (!value) return;

    switch (phase) {
      case 'org-name': {
        addMessage({ sender: 'user', content: value, type: 'text' });
        update({ orgName: value });
        setInputValue('');
        setShowInput(false);

        // API: save org name — await and handle failure gracefully
        (async () => {
          try {
            await apiClient.put('/settings/organization', { name: value });
          } catch {
            // Non-critical — org name can be changed later in settings
            toast.error('Could not save org name — you can update it later in Settings');
          }

          proceedToChannels();
        })();
        break;
      }
      case 'channel-token-input': {
        // User is pasting a channel token (Telegram bot token, etc.)
        const channelId = currentSetupChannel;
        const label = CHANNEL_OPTIONS.find(c => c.id === channelId)?.name || channelId;
        const masked = value.length > 8 ? value.slice(0, 4) + '...' + value.slice(-4) : '****';
        addMessage({ sender: 'user', content: `${label} token: ${masked}`, type: 'text' });
        update({
          channelTokens: { ...data.channelTokens, [channelId]: value },
        });
        setInputValue('');
        setShowInput(false);

        // API: connect channel — on failure, re-show setup card with retry/skip
        if (channelId === 'telegram') {
          (async () => {
            try {
              await channelsService.connectTelegram(value, []);
              botSays(
                [`${label} connected! Nice.`],
                () => processNextChannelSetup(),
              );
            } catch (err: any) {
              const errMsg = err?.response?.data?.message || err?.message || 'Invalid token';
              botSays(
                [`Hmm, that didn't work — ${errMsg}. Want to try again or skip for now?`],
                () => {
                  // Re-show the Telegram setup card so user can retry or skip
                  showChannelSetupInstructions(channelId, channelSetupQueue);
                },
              );
            }
          })();
        } else {
          botSays(
            [`${label} connected! Nice.`],
            () => processNextChannelSetup(),
          );
        }
        break;
      }
      case 'agent-name': {
        addMessage({ sender: 'user', content: value, type: 'text' });
        update({ agentName: value });
        setInputValue('');
        setShowInput(false);
        setPhase('agent-tone');
        botSays(
          [`Love it! And how should ${value} talk? Pick a vibe:`],
          () => {
            addMessage({
              sender: 'bot',
              content: '',
              type: 'options',
              meta: {
                options: [
                  { label: 'Professional', value: 'professional' },
                  { label: 'Friendly', value: 'friendly' },
                  { label: 'Casual', value: 'casual' },
                ],
              },
            });
          },
        );
        break;
      }
      default:
        break;
    }
  };

  // ---- Proceed helpers ----

  const proceedToChannels = () => {
    setPhase('channels');
    botSays(
      [`Nice! ${data.orgName || 'Your workspace'} sounds great. Where do your customers usually reach you? Pick the channels you'd like to set up.`],
      () => {
        addMessage({ sender: 'bot', content: '', type: 'channels' });
      },
    );
  };

  const proceedToKnowledge = () => {
    setPhase('knowledge');
    botSays(
      ["Want to give your agent some knowledge to work with? You can upload files or paste some text."],
      () => {
        addMessage({ sender: 'bot', content: '', type: 'knowledge' });
      },
    );
  };

  const proceedToAgentName = () => {
    setPhase('agent-name');
    botSays(
      ["Almost there! Let's create your first AI agent. What should we call them?"],
      () => {
        setInputMode('text');
        setInputPlaceholder('Give your agent a name...');
        setShowInput(true);
      },
    );
  };

  const proceedToSkills = () => {
    setPhase('skills');
    botSays(
      ["Last thing -- want to add any starter skills?"],
      () => {
        addMessage({ sender: 'bot', content: '', type: 'skills' });
      },
    );
  };

  const proceedToComplete = () => {
    setPhase('complete');
    botSays(
      [
        `You're all set! ${data.orgName} is ready to roll with ${data.agentName || 'your agent'} on board.`,
        "Head to your dashboard to start chatting!",
      ],
      () => {
        addMessage({ sender: 'bot', content: '', type: 'done' });
      },
    );
  };

  // ---- Channel setup flow ----

  const startChannelSetup = (selectedChannels: string[]) => {
    update({ channels: selectedChannels });
    // Build setup queue -- order: telegram, messenger, whatsapp, web
    const setupOrder = ['telegram', 'messenger', 'whatsapp', 'web'];
    const queue = setupOrder.filter(ch => selectedChannels.includes(ch));
    setChannelSetupQueue(queue);

    if (queue.length === 0) {
      proceedToKnowledge();
      return;
    }

    setPhase('channel-setup');
    const first = queue[0];
    setCurrentSetupChannel(first);
    showChannelSetupInstructions(first, queue.slice(1));
  };

  const showChannelSetupInstructions = (channelId: string, remainingQueue: string[]) => {
    setCurrentSetupChannel(channelId);
    setChannelSetupQueue(remainingQueue);
    setPhase('channel-setup');

    const channelName = CHANNEL_OPTIONS.find(c => c.id === channelId)?.name || channelId;

    switch (channelId) {
      case 'telegram':
        botSays(
          [`Great, let's set up ${channelName}! Here's how to get your bot token:`],
          () => {
            addMessage({
              sender: 'bot',
              content: '',
              type: 'channel-setup-telegram',
            });
          },
        );
        break;
      case 'messenger':
        botSays(
          [`Now let's connect ${channelName}. Here's how it works:`],
          () => {
            addMessage({
              sender: 'bot',
              content: '',
              type: 'channel-setup-messenger',
            });
          },
        );
        break;
      case 'whatsapp':
        botSays(
          [`Let's set up ${channelName}! Here's what you need:`],
          () => {
            addMessage({
              sender: 'bot',
              content: '',
              type: 'channel-setup-whatsapp',
            });
          },
        );
        break;
      case 'web':
        botSays(
          [`For the Web Widget, there's nothing to set up right now! We'll generate an embed code for your website once you're done here.`],
          () => {
            addMessage({
              sender: 'bot',
              content: '',
              type: 'channel-setup-web',
            });
          },
        );
        break;
    }
  };

  const processNextChannelSetup = () => {
    if (channelSetupQueue.length > 0) {
      const next = channelSetupQueue[0];
      const rest = channelSetupQueue.slice(1);
      showChannelSetupInstructions(next, rest);
    } else {
      proceedToKnowledge();
    }
  };

  const handleChannelSetupAction = (channelId: string, action: 'connect' | 'skip') => {
    // Remove the setup card
    const setupType = `channel-setup-${channelId}` as MessageType;
    setMessages(prev => prev.filter(m => m.type !== setupType));

    if (action === 'skip') {
      const label = CHANNEL_OPTIONS.find(c => c.id === channelId)?.name || channelId;
      addMessage({ sender: 'user', content: `I'll connect ${label} later`, type: 'text' });
      botSays(
        ['No problem! You can always set it up from the Channels page.'],
        () => processNextChannelSetup(),
      );
    } else {
      // For channels that need a token input
      if (channelId === 'telegram' || channelId === 'whatsapp') {
        setPhase('channel-token-input');
        const placeholder = channelId === 'telegram'
          ? 'Paste your Telegram bot token...'
          : 'Paste your WhatsApp API token...';
        setInputMode('password');
        setInputPlaceholder(placeholder);
        setShowInput(true);
      } else if (channelId === 'messenger') {
        // Meta/Facebook would use OAuth popup -- for now just simulate it
        const label = CHANNEL_OPTIONS.find(c => c.id === channelId)?.name || channelId;
        addMessage({ sender: 'user', content: `Connecting ${label}...`, type: 'text' });
        update({ channelTokens: { ...data.channelTokens, [channelId]: 'oauth-pending' } });
        botSays(
          [`${label} connection initiated! You'll complete the Page selection in your Channels settings.`],
          () => processNextChannelSetup(),
        );
      } else if (channelId === 'web') {
        addMessage({ sender: 'user', content: 'Web Widget ready!', type: 'text' });
        update({ channelTokens: { ...data.channelTokens, [channelId]: 'auto' } });
        processNextChannelSetup();
      }
    }
  };

  // ---- Option pill click handler ----

  const handleOptionClick = (label: string, value: string) => {
    setMessages((prev) => prev.filter((m) => m.type !== 'options'));
    addMessage({ sender: 'user', content: label, type: 'text' });

    if (phase === 'agent-tone') {
      update({ agentTone: value as OnboardingData['agentTone'] });
      proceedToSkills();
    }
  };

  // ---- Channel selection (initial picker) ----

  const handleChannelToggle = (id: string) => {
    setPendingChannels((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );
  };

  const handleChannelContinue = () => {
    const selected = pendingChannels;
    const names = selected.map((id) => CHANNEL_OPTIONS.find((c) => c.id === id)?.name).filter(Boolean);
    setMessages((prev) => prev.filter((m) => m.type !== 'channels'));
    addMessage({
      sender: 'user',
      content: names.length > 0 ? names.join(', ') : "I'll set this up later",
      type: 'text',
    });

    if (selected.length > 0) {
      botSays(
        ["Awesome! Let me walk you through setting each one up."],
        () => startChannelSetup(selected),
      );
    } else {
      update({ channels: [] });
      proceedToKnowledge();
    }
  };

  const handleChannelSkip = () => {
    setMessages((prev) => prev.filter((m) => m.type !== 'channels'));
    addMessage({ sender: 'user', content: "I'll set this up later", type: 'text' });
    proceedToKnowledge();
  };

  // ---- Knowledge ----

  const handleKnowledgeContinue = () => {
    update({ knowledgeFiles: pendingFiles });
    setMessages((prev) => prev.filter((m) => m.type !== 'knowledge'));
    addMessage({
      sender: 'user',
      content: pendingFiles.length > 0 ? `Uploaded ${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''}` : "I'll add knowledge later",
      type: 'text',
    });
    proceedToAgentName();
  };

  const handleKnowledgeSkip = () => {
    setMessages((prev) => prev.filter((m) => m.type !== 'knowledge'));
    addMessage({ sender: 'user', content: "I'll add knowledge later", type: 'text' });
    proceedToAgentName();
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    files.forEach((file) => {
      if (file.size <= 25 * 1024 * 1024) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          const content = ev.target?.result as string;
          setPendingFiles((prev) => [...prev, { name: file.name, content: content.slice(0, 10000) }]);
        };
        reader.readAsText(file);
      }
    });
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const content = ev.target?.result as string;
        setPendingFiles((prev) => [...prev, { name: file.name, content: content.slice(0, 10000) }]);
      };
      reader.readAsText(file);
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleAddPastedText = () => {
    if (!pasteTextInput.trim()) return;
    const count = pasteTextCount + 1;
    setPendingFiles((prev) => [...prev, { name: `Pasted Text ${count}`, content: pasteTextInput.trim() }]);
    setPasteTextInput('');
    setPasteTextCount(count);
  };

  // ---- Skills ----

  const handleSkillToggle = (id: string) => {
    setPendingSkills((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const handleSkillsDone = () => {
    const selected = pendingSkills;
    update({ selectedSkills: selected });
    const names = selected.map((id) => SKILL_OPTIONS.find((s) => s.id === id)?.name).filter(Boolean);
    setMessages((prev) => prev.filter((m) => m.type !== 'skills'));
    addMessage({
      sender: 'user',
      content: names.length > 0 ? names.join(', ') : 'No skills for now',
      type: 'text',
    });
    proceedToComplete();
  };

  const handleSkillsSkip = () => {
    setMessages((prev) => prev.filter((m) => m.type !== 'skills'));
    addMessage({ sender: 'user', content: 'No skills for now', type: 'text' });
    proceedToComplete();
  };

  // ---- Finish ----

  const handleFinish = async () => {
    // Each step is individually wrapped — partial success is preserved

    // 1. Create knowledge base + docs if files uploaded
    let kbId: string | null = null;
    if (data.knowledgeFiles.length > 0) {
      try {
        const kb = await knowledgeService.createKnowledgeBase({
          name: `${data.orgName} Knowledge`,
          description: 'Created during onboarding',
        });
        kbId = kb.id;
        for (const file of data.knowledgeFiles) {
          try {
            await knowledgeService.addDocument(kb.id, {
              name: file.name,
              content: file.content,
              file_type: 'text',
            });
          } catch {
            // Individual doc upload failed — continue with others
          }
        }
      } catch {
        toast.error('Could not create knowledge base — you can add it later');
      }
    }

    // 2. Create agent with KB linked + default greeting + default escalation rule
    let agentId: string | null = null;
    if (data.agentName) {
      try {
        const agent = await agentsService.createAgent({
          name: data.agentName,
          type: 'custom',
          status: 'draft',
          configuration: {
            personality: {
              tone: data.agentTone || 'professional',
              greeting_message: `Hello! I'm ${data.agentName}, how can I help you today?`,
            },
            skills: data.selectedSkills,
            knowledge_base_ids: kbId ? [kbId] : [],
            escalation_rules: [{
              id: `rule-${Date.now()}`,
              name: 'Default Escalation',
              trigger_type: 'keyword',
              trigger_value: '',
              priority: 'normal',
              notification_channels: [],
              assignee_ids: [],
              guideline: 'Escalate when the customer explicitly asks to speak with a human or the issue cannot be resolved.',
            }],
            ai_model: { provider: 'gemini', model: 'gemini-2.5-flash' },
          },
        });
        agentId = agent.id;

        // 3. Try to activate agent (has greeting + KB + escalation)
        if (kbId) {
          try {
            await agentsService.updateAgentStatus(agent.id, 'active');
          } catch {
            // Activation may fail — agent stays draft, user can activate from Agents page
          }
        }
      } catch {
        toast.error('Could not create agent — you can create one from the Agents page');
      }
    }

    // 4. Connect channels with agent assigned (only channels that were already connected during setup)
    // Telegram tokens were already connected during the channel-token-input phase,
    // so we only need to assign the agent if it was just created
    if (agentId) {
      for (const channelId of data.channels) {
        try {
          const token = data.channelTokens[channelId];
          if (channelId === 'telegram' && token) {
            // Token was already connected earlier — this is a no-op if already connected
            // but ensures agent is assigned
            await channelsService.connectTelegram(token, [agentId]);
          }
        } catch {
          // Channel may already be connected — non-blocking
        }
      }
    }

    // 5. Mark onboarding complete
    try {
      await apiClient.put('/settings/onboarding-complete');
    } catch {
      // Non-critical — worst case user sees onboarding again
    }

    navigate('/');
  };

  const handleSkipSetup = async () => {
    try {
      await apiClient.put('/settings/onboarding-complete');
    } catch {
      // Non-blocking
    }
    navigate('/');
  };

  // ---- Copy text helper ----

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  // ---- Progress ----

  const progress = PHASE_PROGRESS[phase] || 10;

  // ---- Render messages ----

  const renderMessage = (msg: ChatMessage) => {
    if (msg.sender === 'user') {
      return <UserBubble key={msg.id}>{msg.content}</UserBubble>;
    }

    switch (msg.type) {
      case 'text':
        return <BotBubble key={msg.id}>{msg.content}</BotBubble>;

      case 'options': {
        const options = (msg.meta?.options || []) as { label: string; value: string }[];
        return (
          <div key={msg.id} className="flex items-end gap-2.5" style={{ animation: 'fadeSlideUp 0.3s ease-out both' }}>
            <div className="w-8 shrink-0" />
            <div className="flex flex-wrap gap-2">
              {options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleOptionClick(opt.label, opt.value)}
                  className="group relative px-5 py-2.5 rounded-full border-2 border-indigo-200 text-indigo-600 bg-white text-sm font-medium hover:border-[#1a1a2e] hover:bg-blue-500 hover:text-white hover:shadow-[4px_4px_0px_#1a1a2e] transition-all duration-200 active:scale-95"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        );
      }

      case 'channels':
        return (
          <div key={msg.id} className="flex items-start gap-2.5" style={{ animation: 'fadeSlideUp 0.3s ease-out both' }}>
            <div className="w-8 shrink-0" />
            <div className="space-y-3 max-w-[85%]">
              <div className="flex flex-wrap gap-2">
                {CHANNEL_OPTIONS.map((ch) => {
                  const selected = pendingChannels.includes(ch.id);
                  return (
                    <button
                      key={ch.id}
                      onClick={() => ch.enabled && handleChannelToggle(ch.id)}
                      disabled={!ch.enabled}
                      className={cn(
                        'flex items-center gap-2.5 px-4 py-3 rounded-xl border-2 transition-all duration-200 text-left bg-white relative',
                        !ch.enabled
                          ? 'border-gray-200 opacity-60 cursor-not-allowed'
                          : selected
                            ? 'border-[#1a1a2e] bg-blue-50 shadow-[3px_3px_0px_#1a1a2e] ring-2 ring-blue-500'
                            : 'border-gray-300 hover:border-[#1a1a2e] hover:shadow-[3px_3px_0px_#1a1a2e] hover:-translate-y-0.5',
                      )}
                    >
                      <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border-2 border-[#1a1a2e]', ch.enabled ? ch.color : 'from-gray-300 to-gray-400')}>
                        <ch.icon className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex flex-col">
                        <p className={cn('text-sm font-semibold', ch.enabled ? 'text-gray-900' : 'text-gray-400')}>{ch.name}</p>
                        {!ch.enabled && <span className="text-[10px] text-gray-400 font-medium">Coming Soon</span>}
                      </div>
                      {selected && ch.enabled && <Check className="w-4 h-4 text-indigo-500 ml-1" />}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleChannelContinue}
                  className="px-5 py-2 rounded-full bg-blue-500 text-white text-sm font-medium border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:bg-blue-600 transition-all active:scale-95"
                >
                  Continue
                </button>
                <button onClick={handleChannelSkip} className="text-sm text-gray-400 hover:text-indigo-500 transition-colors font-medium">
                  Skip for now
                </button>
              </div>
            </div>
          </div>
        );

      // ---- TELEGRAM SETUP ----
      case 'channel-setup-telegram':
        return (
          <div key={msg.id} className="flex items-start gap-2.5" style={{ animation: 'fadeSlideUp 0.3s ease-out both' }}>
            <div className="w-8 shrink-0" />
            <div className="max-w-[85%] bg-white border-2 border-[#1a1a2e] rounded-xl rounded-bl-md shadow-[3px_3px_0px_#1a1a2e] p-5 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-sky-500 border-2 border-[#1a1a2e] flex items-center justify-center shadow-[3px_3px_0px_#1a1a2e]">
                  <Send className="w-4 h-4 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 text-sm">Telegram Bot Setup</h4>
              </div>

              <div className="space-y-3">
                <InstructionStep
                  step={1}
                  text="Open Telegram and search for"
                  highlight="@BotFather"
                />
                <InstructionStep
                  step={2}
                  text="Send the command"
                  highlight="/newbot"
                />
                <InstructionStep
                  step={3}
                  text="Follow the prompts — give your bot a name and username"
                />
                <InstructionStep
                  step={4}
                  text="BotFather will send you a token like"
                  highlight="123456:ABC-DEF..."
                />
                <InstructionStep
                  step={5}
                  text="Copy that token and paste it below"
                />
              </div>

              <div className="flex items-center gap-2 px-3 py-2 bg-sky-50/80 border border-sky-100 rounded-lg">
                <ExternalLink className="w-3.5 h-3.5 text-sky-500 shrink-0" />
                <span className="text-xs text-sky-700">
                  Open Telegram and search <strong>@BotFather</strong> to get started
                </span>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleChannelSetupAction('telegram', 'connect')}
                  className="px-5 py-2 rounded-full bg-sky-500 text-white text-sm font-medium border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:bg-sky-600 transition-all active:scale-95"
                >
                  I have my token
                </button>
                <button
                  onClick={() => handleChannelSetupAction('telegram', 'skip')}
                  className="text-sm text-gray-400 hover:text-indigo-500 transition-colors font-medium"
                >
                  I'll do this later
                </button>
              </div>
            </div>
          </div>
        );

      // ---- MESSENGER / META SETUP ----
      case 'channel-setup-messenger':
        return (
          <div key={msg.id} className="flex items-start gap-2.5" style={{ animation: 'fadeSlideUp 0.3s ease-out both' }}>
            <div className="w-8 shrink-0" />
            <div className="max-w-[85%] bg-white border-2 border-[#1a1a2e] rounded-xl rounded-bl-md shadow-[3px_3px_0px_#1a1a2e] p-5 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-violet-500 border-2 border-[#1a1a2e] flex items-center justify-center shadow-[3px_3px_0px_#1a1a2e]">
                  <MessageSquare className="w-4 h-4 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 text-sm">Facebook Messenger Setup</h4>
              </div>

              <div className="space-y-3">
                <InstructionStep
                  step={1}
                  text="Click 'Connect with Facebook' below"
                />
                <InstructionStep
                  step={2}
                  text="Log in to your Facebook account (if not already)"
                />
                <InstructionStep
                  step={3}
                  text="Select the Facebook Page you want to connect"
                />
                <InstructionStep
                  step={4}
                  text="Grant the required permissions for messaging"
                />
                <InstructionStep
                  step={5}
                  text="You're done! Messages to your Page will be handled by your agent"
                />
              </div>

              <div className="flex items-center gap-2 px-3 py-2 bg-violet-50/80 border border-violet-100 rounded-lg">
                <HelpCircle className="w-3.5 h-3.5 text-violet-500 shrink-0" />
                <span className="text-xs text-violet-700">
                  You need to be an admin of the Facebook Page you want to connect
                </span>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleChannelSetupAction('messenger', 'connect')}
                  className="px-5 py-2 rounded-full bg-violet-500 text-white text-sm font-medium border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:bg-violet-600 transition-all active:scale-95 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.907 1.196 5.433 3.14 7.138V22l3.487-1.914c.932.258 1.921.396 2.946.396h.427C17.523 20.482 22 16.337 22 11.243 22 6.145 17.523 2 12 2z" />
                  </svg>
                  Connect with Facebook
                </button>
                <button
                  onClick={() => handleChannelSetupAction('messenger', 'skip')}
                  className="text-sm text-gray-400 hover:text-indigo-500 transition-colors font-medium"
                >
                  I'll do this later
                </button>
              </div>
            </div>
          </div>
        );

      // ---- WHATSAPP SETUP ----
      case 'channel-setup-whatsapp':
        return (
          <div key={msg.id} className="flex items-start gap-2.5" style={{ animation: 'fadeSlideUp 0.3s ease-out both' }}>
            <div className="w-8 shrink-0" />
            <div className="max-w-[85%] bg-white border-2 border-[#1a1a2e] rounded-xl rounded-bl-md shadow-[3px_3px_0px_#1a1a2e] p-5 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-green-500 border-2 border-[#1a1a2e] flex items-center justify-center shadow-[3px_3px_0px_#1a1a2e]">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 text-sm">WhatsApp Business Setup</h4>
              </div>

              <div className="space-y-3">
                <InstructionStep
                  step={1}
                  text="Go to Meta Business Suite"
                  highlight="business.facebook.com"
                />
                <InstructionStep
                  step={2}
                  text="Navigate to WhatsApp > API Setup in the left menu"
                />
                <InstructionStep
                  step={3}
                  text="Create or select your WhatsApp Business Account"
                />
                <InstructionStep
                  step={4}
                  text="Generate a Permanent Access Token from API Setup"
                />
                <InstructionStep
                  step={5}
                  text="Copy the token and paste it below"
                />
              </div>

              <button
                onClick={() => copyToClipboard('https://business.facebook.com/wa/manage/phone-numbers/')}
                className="flex items-center gap-2 px-3 py-2 bg-green-50/80 border border-green-100 rounded-lg hover:bg-green-100 transition-colors w-full"
              >
                <Copy className="w-3.5 h-3.5 text-green-600 shrink-0" />
                <span className="text-xs text-green-700 truncate flex-1">
                  business.facebook.com/wa/manage/phone-numbers/
                </span>
                <ExternalLink className="w-3 h-3 text-green-400 ml-auto shrink-0" />
              </button>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleChannelSetupAction('whatsapp', 'connect')}
                  className="px-5 py-2 rounded-full bg-green-500 text-white text-sm font-medium border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:bg-green-600 transition-all active:scale-95"
                >
                  I have my token
                </button>
                <button
                  onClick={() => handleChannelSetupAction('whatsapp', 'skip')}
                  className="text-sm text-gray-400 hover:text-indigo-500 transition-colors font-medium"
                >
                  I'll do this later
                </button>
              </div>
            </div>
          </div>
        );

      // ---- WEB WIDGET SETUP ----
      case 'channel-setup-web':
        return (
          <div key={msg.id} className="flex items-start gap-2.5" style={{ animation: 'fadeSlideUp 0.3s ease-out both' }}>
            <div className="w-8 shrink-0" />
            <div className="max-w-[85%] bg-white border-2 border-[#1a1a2e] rounded-xl rounded-bl-md shadow-[3px_3px_0px_#1a1a2e] p-5 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-500 border-2 border-[#1a1a2e] flex items-center justify-center shadow-[3px_3px_0px_#1a1a2e]">
                  <Globe className="w-4 h-4 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 text-sm">Web Widget</h4>
              </div>

              <p className="text-sm text-gray-600 leading-relaxed">
                Your web chat widget will be ready automatically! After setup, go to <strong>Channels</strong> in the sidebar to grab the embed code and paste it into your website.
              </p>

              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50/80 border border-blue-100 rounded-lg">
                <Check className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                <span className="text-xs text-blue-700">No setup needed — widget is auto-generated</span>
              </div>

              <button
                onClick={() => handleChannelSetupAction('web', 'connect')}
                className="px-5 py-2 rounded-full bg-blue-500 text-white text-sm font-medium border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:bg-blue-600 transition-all active:scale-95"
              >
                Got it, continue
              </button>
            </div>
          </div>
        );

      case 'knowledge':
        return (
          <div key={msg.id} className="flex items-start gap-2.5" style={{ animation: 'fadeSlideUp 0.3s ease-out both' }}>
            <div className="w-8 shrink-0" />
            <div className="space-y-3 max-w-[85%]">
              <div
                className={cn(
                  'border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 cursor-pointer bg-white',
                  dragActive
                    ? 'border-[#1a1a2e] bg-blue-50 shadow-[3px_3px_0px_#1a1a2e]'
                    : 'border-gray-300 hover:border-[#1a1a2e] hover:shadow-[3px_3px_0px_#1a1a2e] hover:-translate-y-0.5',
                )}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 border-2 border-[#1a1a2e] flex items-center justify-center mx-auto mb-2">
                  <Upload className="w-5 h-5 text-indigo-500" />
                </div>
                <p className="text-sm text-gray-700 font-medium">Drop files or click to browse</p>
                <p className="text-xs text-gray-400 mt-1">PDF, TXT, MD, DOCX, CSV</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.txt,.md,.docx,.doc,.csv,.html,.json,.xml"
                  onChange={handleFileInput}
                  className="hidden"
                />
              </div>

              {/* Or divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200/80" />
                <span className="text-xs text-gray-400 font-medium">or paste text</span>
                <div className="flex-1 h-px bg-gray-200/80" />
              </div>

              {/* Paste Text Area */}
              <div className="flex gap-2">
                <textarea
                  value={pasteTextInput}
                  onChange={(e) => setPasteTextInput(e.target.value)}
                  placeholder="Paste FAQ, product info, policies..."
                  rows={3}
                  className="flex-1 px-3 py-2 text-sm text-gray-900 bg-white border-2 border-[#1a1a2e] rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none placeholder:text-gray-400"
                />
                <button
                  onClick={handleAddPastedText}
                  disabled={!pasteTextInput.trim()}
                  className={cn(
                    'self-end px-3 py-2 rounded-lg text-sm font-medium transition-all shrink-0',
                    pasteTextInput.trim()
                      ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200 active:scale-95'
                      : 'bg-gray-100 text-gray-300 cursor-not-allowed',
                  )}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              {pendingFiles.length > 0 && (
                <div className="space-y-1.5">
                  {pendingFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg border-2 border-[#1a1a2e]">
                      {file.name.startsWith('Pasted Text') ? (
                        <Type className="w-4 h-4 text-indigo-500 shrink-0" />
                      ) : (
                        <FileText className="w-4 h-4 text-indigo-500 shrink-0" />
                      )}
                      <span className="text-sm text-gray-700 truncate flex-1">{file.name}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={handleKnowledgeContinue}
                  className="px-5 py-2 rounded-full bg-blue-500 text-white text-sm font-medium border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:bg-blue-600 transition-all active:scale-95"
                >
                  Continue
                </button>
                <button onClick={handleKnowledgeSkip} className="text-sm text-gray-400 hover:text-indigo-500 transition-colors font-medium">
                  Skip
                </button>
              </div>
            </div>
          </div>
        );

      case 'skills':
        return (
          <div key={msg.id} className="flex items-start gap-2.5" style={{ animation: 'fadeSlideUp 0.3s ease-out both' }}>
            <div className="w-8 shrink-0" />
            <div className="space-y-3 max-w-[85%]">
              <div className="flex flex-wrap gap-2">
                {SKILL_OPTIONS.map((skill) => {
                  const selected = pendingSkills.includes(skill.id);
                  return (
                    <button
                      key={skill.id}
                      onClick={() => handleSkillToggle(skill.id)}
                      className={cn(
                        'flex items-center gap-2 px-4 py-2.5 rounded-full border-2 text-sm font-medium transition-all duration-200 active:scale-95',
                        selected
                          ? 'border-[#1a1a2e] bg-blue-500 text-white shadow-[3px_3px_0px_#1a1a2e]'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-[#1a1a2e] hover:shadow-[3px_3px_0px_#1a1a2e] hover:-translate-y-0.5',
                      )}
                    >
                      <skill.icon className={cn('w-4 h-4', selected ? 'text-white/90' : 'text-indigo-400')} />
                      {skill.name}
                      {selected && <Check className="w-3.5 h-3.5 text-white/90" />}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSkillsDone}
                  className="px-5 py-2 rounded-full bg-blue-500 text-white text-sm font-medium border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:bg-blue-600 transition-all active:scale-95"
                >
                  Done
                </button>
                <button onClick={handleSkillsSkip} className="text-sm text-gray-400 hover:text-indigo-500 transition-colors font-medium">
                  Skip
                </button>
              </div>
            </div>
          </div>
        );

      case 'done':
        return (
          <div key={msg.id} className="flex items-start gap-2.5" style={{ animation: 'fadeSlideUp 0.4s ease-out both' }}>
            <div className="w-8 shrink-0" />
            <div className="relative">
              <button
                onClick={handleFinish}
                className="group relative flex items-center gap-3 px-8 py-4 rounded-xl bg-blue-500 text-white font-semibold text-lg border-2 border-[#1a1a2e] shadow-[5px_5px_0px_#1a1a2e] hover:bg-blue-600 transition-all duration-300 active:scale-[0.98]"
              >
                Go to Dashboard
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleInputSubmit();
    }
  };

  const isComplete = phase === 'complete';

  return (
    <>
      <style>{`
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        @keyframes float1 {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
        }
        @keyframes float2 {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(15px) translateX(-15px); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="h-screen flex flex-col bg-[#FFFDF7] relative overflow-hidden">
        {/* Decorative elements removed for neobrutalism */}

        {/* Top bar */}
        <div className="relative flex items-center justify-between px-5 py-3.5 bg-white z-10 border-b-2 border-[#1a1a2e]">
          <div className="flex items-center -space-x-0.5">
            <img src="/logo-icon.svg" alt="ChatCup" className="h-9" />
            <img src="/logo-name.svg" alt="ChatCup.ai" className="h-5" />
          </div>

          {/* Progress bar */}
          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gray-100/60">
            <div
              className="h-full bg-blue-500 transition-all duration-700 ease-out rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>

          {!isComplete && (
            <button onClick={handleSkipSetup} className="text-sm text-gray-400 hover:text-indigo-500 transition-colors font-medium">
              Skip setup
            </button>
          )}
        </div>

        {/* Chat area */}
        <div className="flex-1 overflow-y-auto relative z-[1]">
          <div className="max-w-xl mx-auto px-4 py-6 space-y-4">
            {messages.map(renderMessage)}
            {isTyping && <TypingIndicator />}
            <div ref={chatEndRef} />
          </div>
        </div>

        {/* Input bar */}
        {showInput && !isComplete && (
          <div className="relative z-[1] border-t-2 border-[#1a1a2e] bg-white px-4 py-3">
            <div className="max-w-xl mx-auto flex items-center gap-2">
              <input
                ref={inputRef}
                type={inputMode === 'password' ? 'password' : 'text'}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={inputPlaceholder}
                className="flex-1 h-11 px-4 rounded-xl border-2 border-[#1a1a2e] bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                autoComplete="off"
              />
              <button
                onClick={handleInputSubmit}
                disabled={!inputValue.trim()}
                className={cn(
                  'w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-200 shrink-0',
                  inputValue.trim()
                    ? 'bg-blue-500 text-white border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:bg-blue-600 active:scale-95'
                    : 'bg-gray-100/80 text-gray-300 cursor-not-allowed',
                )}
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
